import axios from 'axios'
import moment from 'moment'
import pubsub from 'pubsub-js'
import React, { useCallback, useEffect, useState } from 'react'
import { Badge, Button, Card, CardBody, Col, Input, Row } from 'reactstrap'
import Swal from 'sweetalert2'
import { albanianMonths } from './enums/AlbanianMonths'

const STATUS_LABELS = {
  open: { text: 'Pa paga të procesuara', color: 'secondary' },
  processing: { text: 'E procesuar – e papublikuar', color: 'warning' },
  published: { text: 'E publikuar', color: 'success' }
}

// Salary month M covers 26.(M-1) – 25.M, so until the 25th we are still
// paying out the previous month.
const defaultSalaryMonth = () => {
  const today = moment()
  return (today.date() <= 25 ? today.subtract(1, 'month') : today).format('YYYY-MM')
}

const showError = (error) =>
  pubsub.publish('showNotification', {
    message: error?.response?.data?.message || error?.message || 'Ndodhi një gabim',
    type: 'error'
  })

function PayrollPublishBar() {
  const [selectedMonth, setSelectedMonth] = useState(defaultSalaryMonth())
  const [period, setPeriod] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const [year, month] = selectedMonth.split('-').map(Number)
  const monthLabel = `${albanianMonths[month - 1]} ${year}`

  const fetchPeriod = useCallback(async () => {
    if (!year || !month) return
    try {
      const { data } = await axios.get(`payrollPeriods/${year}/${month}`)
      if (data.success) setPeriod(data.data)
    } catch (error) {
      showError(error)
    }
  }, [year, month])

  useEffect(() => {
    fetchPeriod()
  }, [fetchPeriod])

  const runAction = async (action, confirmText, successText) => {
    const result = await Swal.fire({
      title: monthLabel,
      text: confirmText,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Po',
      cancelButtonText: 'Anulo'
    })
    if (!result.isConfirmed) return

    setIsLoading(true)
    try {
      const { data } = await axios.post(`payrollPeriods/${year}/${month}/${action}`)
      if (!data.success) return showError({ message: data.message })
      pubsub.publish('showNotification', { message: successText, type: 'success' })
      await fetchPeriod()
    } catch (error) {
      showError(error)
    } finally {
      setIsLoading(false)
    }
  }

  const status = period?.status || 'open'

  return (
    <Card className='shadow-none bg-white mb-3'>
      <CardBody className={isLoading ? 'whirl standard' : null}>
        <Row className='align-items-center'>
          <Col md='auto'>
            <label className='col-form-label mr-2'>Publikimi i pagave për muajin:</label>
          </Col>
          <Col md={2}>
            <Input type='month' value={selectedMonth} onChange={(e) => e.target.value && setSelectedMonth(e.target.value)} />
          </Col>
          <Col md='auto'>
            <Badge color={STATUS_LABELS[status].color} className='p-2'>
              {STATUS_LABELS[status].text}
            </Badge>
          </Col>
          <Col className='text-right'>
            {status === 'processing' && (
              <Button
                color='success'
                onClick={() =>
                  runAction(
                    'publish',
                    `Punonjësit do të shohin pagat dhe fletëpagesat e këtij muaji${
                      period?.pending_notifications ? ` dhe ${period.pending_notifications} prej tyre do të njoftohen` : ''
                    }.`,
                    'Pagat u publikuan'
                  )
                }
              >
                Publiko
              </Button>
            )}
            {status === 'published' && (
              <Button
                color='secondary'
                onClick={() =>
                  runAction(
                    'reopen',
                    'Punonjësit që janë publikuar më parë do të vazhdojnë të shohin pagën e këtij muaji, përfshirë çdo ndryshim nga procesimi i ri. Punonjësit e rinj mbeten të fshehur derisa të publikohet përsëri.',
                    'Muaji u rihap për procesim'
                  )
                }
              >
                Rihap muajin
              </Button>
            )}
          </Col>
        </Row>
        {status === 'open' && (
          <small className='text-muted d-block mt-2'>
            Ende nuk ka paga të procesuara për këtë muaj. Pas procesimit, pagat mbeten të fshehura derisa të shtypni Publiko.
          </small>
        )}
        {status === 'processing' && period?.published_at && (
          <small className='text-muted d-block mt-2'>
            Muaji është rihapur: {period.published_users || 0} punonjës të publikuar më parë vazhdojnë ta shohin pagën e tyre.
            Punonjësit e rinj do ta shohin pasi të shtypni Publiko.
          </small>
        )}
        {status === 'published' && period?.published_at && (
          <small className='text-muted d-block mt-2'>
            Publikuar më {moment(period.published_at).format('DD.MM.YYYY HH:mm')}
            {period.published_by ? ` nga ${period.published_by.first_name} ${period.published_by.last_name}` : ''}
          </small>
        )}
      </CardBody>
    </Card>
  )
}

export default PayrollPublishBar
